'use client';

import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const DebtPreviewModal = ({ debt, closeModal }) => {
	const getStatusColor = (status) => {
		switch (status) {
			case 'PAID':
				return 'status-paid';
			case 'PENDING':
				return 'status-pending';
			case 'DEFAULTED':
				return 'status-defaulted';
			case 'PARTIALLY_PAID':
				return 'status-partially-paid';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	const getPriorityColor = (priority) => {
		switch (priority) {
			case 1:
				return 'priority-high';
			case 2:
				return 'priority-medium';
			case 3:
				return 'priority-low';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	const getPriorityText = (priority) => {
		switch (priority) {
			case 1:
				return 'High';
			case 2:
				return 'Medium';
			case 3:
				return 'Low';
			default:
				return 'Unknown';
		}
	};

	return (
		<div className='fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50'>
			<div className='bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto'>
				<div className='flex items-center justify-between p-6 border-b border-gray-200'>
					<h2 className='text-xl font-semibold text-gray-900'>
						Debt Details
					</h2>
					<button
						onClick={closeModal}
						className='text-gray-400 hover:text-gray-500'>
						<XMarkIcon className='h-6 w-6' />
					</button>
				</div>

				<div className='p-6 space-y-6'>
					{/* Debt Overview */}
					<div className='bg-gradient-to-r from-primary-50 to-primary-100 p-4 rounded-lg'>
						<div className='flex items-center justify-between'>
							<div>
								<h3 className='text-lg font-semibold text-primary-900'>
									₦{debt.amount.toLocaleString()}
								</h3>
								<p className='text-sm text-primary-700'>
									Due: {format(new Date(debt.dueDate), 'MMM dd, yyyy')}
								</p>
							</div>
							<div className='text-right'>
								<span className={`status-badge ${getStatusColor(debt.status)}`}>
									{debt.status}
								</span>
								<div className='mt-2'>
									<span className={`status-badge ${getPriorityColor(debt.priority)}`}>
										{getPriorityText(debt.priority)} Priority
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Customer Information */}
					{debt.user && (
						<div>
							<h3 className='text-lg font-medium text-gray-900 mb-4'>
								Customer Information
							</h3>
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'>
								<div>
									<span className='font-medium text-gray-500'>Name:</span>
									<p className='text-gray-900'>
										{debt.user.firstName} {debt.user.lastName}
									</p>
								</div>
								<div>
									<span className='font-medium text-gray-500'>Email:</span>
									<p className='text-gray-900'>{debt.user.email}</p>
								</div>
								<div>
									<span className='font-medium text-gray-500'>Phone:</span>
									<p className='text-gray-900'>{debt.user.phoneNumber}</p>
								</div>
								<div>
									<span className='font-medium text-gray-500'>BVN:</span>
									<p className='text-gray-900'>{debt.user.bvn}</p>
								</div>
							</div>
						</div>
					)}

					{/* Debt Information */}
					<div>
						<h3 className='text-lg font-medium text-gray-900 mb-4'>
							Debt Information
						</h3>
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'>
							<div>
								<span className='font-medium text-gray-500'>Amount:</span>
								<p className='text-gray-900'>₦{debt.amount.toLocaleString()}</p>
							</div>
							<div>
								<span className='font-medium text-gray-500'>Due Date:</span>
								<p className='text-gray-900'>
									{format(new Date(debt.dueDate), 'MMM dd, yyyy')}
								</p>
							</div>
							<div>
								<span className='font-medium text-gray-500'>Priority:</span>
								<p className='text-gray-900'>{getPriorityText(debt.priority)}</p>
							</div>
							<div>
								<span className='font-medium text-gray-500'>Status:</span>
								<p className='text-gray-900'>{debt.status}</p>
							</div>
							<div>
								<span className='font-medium text-gray-500'>Creditor Account:</span>
								<p className='text-gray-900'>{debt.creditorAccount}</p>
							</div>
							<div>
								<span className='font-medium text-gray-500'>Created:</span>
								<p className='text-gray-900'>
									{format(new Date(debt.createdAt), 'MMM dd, yyyy HH:mm')}
								</p>
							</div>
						</div>
					</div>

					{/* Additional Information */}
					{debt.updatedAt && debt.updatedAt !== debt.createdAt && (
						<div>
							<h3 className='text-lg font-medium text-gray-900 mb-4'>
								Update History
							</h3>
							<div className='text-sm'>
								<span className='font-medium text-gray-500'>Last Updated:</span>
								<p className='text-gray-900'>
									{format(new Date(debt.updatedAt), 'MMM dd, yyyy HH:mm')}
								</p>
							</div>
						</div>
					)}
				</div>

				<div className='flex items-center justify-end p-6 border-t border-gray-200'>
					<button
						onClick={closeModal}
						className='btn-primary'>
						Close
					</button>
				</div>
			</div>
		</div>
	);
};

export default DebtPreviewModal;